import {
  dataSetToAttributes,
  SkeletonEditorialGalleryCard,
} from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';

import { useContentfulQuery } from '../../hooks/useContentfulQuery';
import { EditorialGalleryCard } from '../EditorialGalleryCard';
import { defaultEditorialTilesPerPage } from './constants';
import type { TileQueryResponse } from './query';
import { tileCollectionQuery } from './query';
import { galleryPlaceholderCss } from './styles';
import type { LazyTilesProps } from './types';

export const EditorialGalleryLazyTiles: FC<LazyTilesProps> = ({
  emptyPlaceholder,
  tiles,
  emptyPlaceholderDataset,
  isGalleryLoading,
  defaultTileImage,
  numberOfTiles,
}) => {
  const tileIds = tiles?.map(tile => tile.tileSysId) ?? [];

  const { data, loading } = useContentfulQuery<TileQueryResponse, { ids: string[] }>(
    tileCollectionQuery,
    {
      variables: { ids: tileIds },
      skip: tileIds.length === 0 || isGalleryLoading,
    }
  );

  const sortedItems = data?.tileCollection?.items
    .slice()
    .sort((a, b) => tileIds.indexOf(a.sys.id) - tileIds.indexOf(b.sys.id));

  if (isGalleryLoading || loading) {
    return (
      <>
        {Array.from({ length: numberOfTiles ?? defaultEditorialTilesPerPage }).map((_, index) => (
          <SkeletonEditorialGalleryCard key={`skeleton-editorial-gallery-card-${index}`} />
        ))}
      </>
    );
  }

  return (
    <>
      {sortedItems?.length ? (
        sortedItems.map(tile => {
          return (
            <EditorialGalleryCard
              key={tile.sys.id}
              {...tile}
              media={tile.media ?? defaultTileImage}
            />
          );
        })
      ) : (
        <div data-testid="mwp-editorial-gallery-placeholder" className={galleryPlaceholderCss}>
          <p {...dataSetToAttributes(emptyPlaceholderDataset)}>{emptyPlaceholder}</p>
        </div>
      )}
    </>
  );
};

EditorialGalleryLazyTiles.displayName = 'EditorialGalleryLazyTiles';
